'use client';

import { $createParagraphNode, $createTextNode, $getRoot, $insertNodes, createEditor, LexicalEditor, type EditorState } from 'lexical';
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateNodesFromDOM, $generateHtmlFromNodes,  } from '@lexical/html';
import { useEffect, useRef, useState } from "react";
import { theme as cleanTheme } from '../themes/clean/classes';
import { DEFAULT_NODES } from '../defaultNodes';
import { type ResolvedLexicalEditorProfile } from '../profileTypes';

function normalizeExportedHtml(html: string): string
{
    // 1) Remove editor-only classes (editor-*) and direction helpers (ltr/rtl) from ANY tag
    let normalizedHtml = html.replace(/<([a-zA-Z0-9:-]+)\b([^>]*?)class="([^"]*?)"([^>]*)>/g, (m, tag, before, classes, after) =>
    {
        const kept = classes
            .split(/\s+/)
            .filter((cls: string) => cls && !/^editor-/.test(cls) && cls !== 'ltr' && cls !== 'rtl')
            .join(' ');
        if (kept.length > 0)
        {
            return `<${tag}${before}class="${kept}"${after}>`;
        }
        // drop the class attribute entirely if empty
        const beforeTrim = (before || '').replace(/\s+$/, '');
        const afterTrim = (after || '').replace(/^\s+/, '');
        return `<${tag}${beforeTrim}${afterTrim}>`;
    });

    // 2) Ensure <br/> self-closing form for consistency with input samples
    normalizedHtml = normalizedHtml.replace(/<br\s*\/?>(?![^<]*>)/g, '<br/>');
    normalizedHtml = normalizedHtml.replace(/<br\s*>/g, '<br/>');

    // 3) Ensure <img/> self-closing form
    normalizedHtml = normalizedHtml.replace(/<img\b([^>]*?)>/g, (m, attrs: string) =>
    {
        // If already self-closed, keep as is
        return /\/\s*$/.test(attrs) ? `<img${attrs}>` : `<img${attrs}/>`;
    });

    return normalizedHtml;
}

export function setEditorStateFromText(editor: LexicalEditor, text: string)
{
    editor.update(() =>
    {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        const textNode = $createTextNode(text);
        paragraph.append(textNode);
        root.append(paragraph);
    });
}

export function setEditorStateFromSerializedStateJSON(editor: LexicalEditor, json: string)
{
    editor.update(() =>
    {
        try
        {
            const serializedState = JSON.parse(json);
            const editorState = editor.parseEditorState(serializedState);
            editor.setEditorState(editorState);
        }
        catch (error)
        {
            // TODO
        }
    });
}

// export function lexicalEditorValueToHtml(value: any)
// {
//     if (typeof value === 'string')
//     {
//         const { html } = parseLexicalValue(value);
//         return html;
//     }
//     if (typeof value === 'object' && value !== null && 'state' in value && 'html' in value)
//     {
//         return value.html;
//     }
//     return '';
// }

// export function serializeLexicalValue(value: any)
// {
//     if (typeof value === 'object' && value !== null && 'format' in value && 'value' in value)
//     {
//         return 'lexical:' + JSON.stringify(value);
//     }
//     return '';
// }

export function parseLexicalValueString(input: string): { state: string | undefined, html: string }
{
    let html = input;
    let state;

    if (html.startsWith('lexical:'))
    {
        html = html.substring(8);

        if (html && html.length > 0 && html.startsWith('{'))
        {
            try
            {
                const json = JSON.parse(html);
                const parsedValue = validateLexicalValue(json);
                if (parsedValue) { return parsedValue; }
            }
            catch (error) {  /* ignore */  }
        }
    }

    return { state, html };
}

export function validateLexicalValue(input: object): { state: string | undefined, html: string } | undefined
{
    if (typeof input === 'object' && input !== null && 'state' in input)
    {
        if ('html' in input)
        {
            const { state, html } = input as { state: string, html: string };
            return { state, html };
        }
        else if ('htmlRaw' in input)
        {
            const { state, htmlRaw: html } = input as { state: string, htmlRaw: string };
            return { state, html };
        }
    }
}

export function parseOrValidateLexicalValue(input: string | object): { state: string | undefined, html: string } | undefined
{
    return typeof input === 'string' ? parseLexicalValueString(input) : validateLexicalValue(input);
}

export function exportHtmlFromState
(
    state: EditorState,
    cancel: () => boolean,
    result: (html: string) => void,
    resolvedProfile?: ResolvedLexicalEditorProfile
)
: void
{
    if (cancel()) { return; }
    const headless = createEditor
    ({
        nodes: resolvedProfile?.nodes ?? DEFAULT_NODES,
        theme: resolvedProfile?.exportTheme ?? cleanTheme,
        editable: false,
        html: resolvedProfile?.exportMap
            ? { export: resolvedProfile.exportMap }
            // eslint-disable-next-line no-undefined
            : undefined,
    });
    headless.setEditorState(state);
    headless.getEditorState().read(() =>
    {
        const html = $generateHtmlFromNodes(headless, null);
        if (cancel()) { return; }
        result(html);
    });
}

export type EditorStatePluginProperties =
{
    initialValue: string,
    onChange: (value: string) => void,
    resolvedProfile?: ResolvedLexicalEditorProfile
}

export function EditorStatePlugin
(
    {
        initialValue: value,
        onChange,
        resolvedProfile
    }
    : EditorStatePluginProperties
)
{
    const aliveRef = useRef(true);
    const [editor] = useLexicalComposerContext();
    const [isFirstRender, setIsFirstRender] = useState(true);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const updateListenerVersionRef = useRef(0);
    const idleHandleRef = useRef<number | null>(null);
    const scheduleIdle = (fn: () => void) =>
    {
        if (idleHandleRef.current != null) { cancelIdleCallback(idleHandleRef.current); }
        idleHandleRef.current = requestIdleCallback(fn, { timeout: 300 });
    };
    useEffect(() =>
    {
        aliveRef.current = true;
        if (editor && isFirstRender)
        {
            setIsFirstRender(false);
            if (typeof value === 'string')
            {
                const { state, html } = parseLexicalValueString(value);
                // if (value.startsWith('lexical:'))
                // eslint-disable-next-line no-undefined
                if (state !== undefined)
                {
                    // const lexicalValue = JSON.parse(value.substring(8));
                    setEditorStateFromSerializedStateJSON(editor, state);
                }
                else
                {
                    const off = editor.registerUpdateListener(({ editorState, tags }) =>
                    {
                        if (tags.has('connectico-import'))
                        {
                            off();
                            exportHtmlFromState(editorState, () => false, (htmlAfter) =>
                            {
                                console.debug('[Lexical import] after:', htmlAfter);
                            });
                        }
                    });
                    editor.update(() =>
                    {
                        const root = $getRoot();
                        root.clear();
                        const parser = new DOMParser();
                        const dom = parser.parseFromString(html, 'text/html');

                        // Method 1
                        // const nodes = $generateNodesFromDOM(editor, dom);
                        // const selection = $getRoot().select();
                        // if (!selection) { return; }
                        // $insertGeneratedNodes(editor, nodes, selection);

                        // Method 2
                        // const nodes = $generateNodesFromDOM(editor, dom);
                        // nodes.forEach((n) => root.append(n));

                        // Method 3
                        const nodes = $generateNodesFromDOM(editor, dom);
                        const topLevel = nodes; // nodes.filter(n => n.getParent() === null);
                        $insertNodes(topLevel);

                        // const htmlAfterRaw = $generateHtmlFromNodes(editor, null);

                        console.debug('[Lexical import] before:', value);
                    },
                    { tag: 'connectico-import' });
                }
            }
        }
        const unregister = editor.registerUpdateListener(() =>
        {
            const updateListenerVersion = ++updateListenerVersionRef.current;
            scheduleIdle(() =>
            {
                if (!aliveRef.current) { return; }
                exportHtmlFromState
                (
                    editor.getEditorState(),
                    () => updateListenerVersion !== updateListenerVersionRef.current,
                    html =>
                    {
                        if (!aliveRef.current) { return; }
                        onChangeRef.current('lexical:' + html);
                    },
                    resolvedProfile
                );
            });
        });
        return () =>
        {
            aliveRef.current = false;
            if (idleHandleRef.current != null)
            {
                cancelIdleCallback(idleHandleRef.current);
                idleHandleRef.current = null;
            }
            unregister();
        };
    },
    [editor, isFirstRender, value, onChange, resolvedProfile]);
    return null;
}